import type {
  BusinessHourDayKey,
  BusinessHours,
  BusinessHoursDay,
  PublicHolidayHours,
} from "@/lib/types";

export const BUSINESS_HOURS_DAYS: {
  key: BusinessHourDayKey;
  shortLabel: string;
  label: string;
}[] = [
  { key: "sunday", shortLabel: "Sun", label: "Sunday" },
  { key: "monday", shortLabel: "Mon", label: "Monday" },
  { key: "tuesday", shortLabel: "Tue", label: "Tuesday" },
  { key: "wednesday", shortLabel: "Wed", label: "Wednesday" },
  { key: "thursday", shortLabel: "Thu", label: "Thursday" },
  { key: "friday", shortLabel: "Fri", label: "Friday" },
  { key: "saturday", shortLabel: "Sat", label: "Saturday" },
];

const DAY_KEYS: BusinessHourDayKey[] = BUSINESS_HOURS_DAYS.map((d) => d.key);

const SHORT_WEEKDAY_TO_INDEX: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export type BusinessOpenStatus = {
  isOpen: boolean;
  tone: "open" | "closed" | "unknown";
  label: string;
  detail: string;
  fullText: string;
};

function makeDay(isOpen: boolean): BusinessHoursDay {
  return {
    isOpen,
    openMinutes: isOpen ? 8 * 60 : null,
    closeMinutes: isOpen ? 18 * 60 : null,
  };
}

function makePublicHolidayDefault(): PublicHolidayHours {
  return {
    mode: "closed",
    customDay: {
      isOpen: true,
      openMinutes: 9 * 60,
      closeMinutes: 16 * 60,
    },
  };
}

export function createDefaultBusinessHours(): BusinessHours {
  return {
    timezone: "Africa/Nairobi",
    sameHoursEveryDay: true,
    is24Hours: false,
    temporarilyClosed: false,
    weekly: {
      sunday: makeDay(false),
      monday: makeDay(true),
      tuesday: makeDay(true),
      wednesday: makeDay(true),
      thursday: makeDay(true),
      friday: makeDay(true),
      saturday: makeDay(true),
    },
    publicHolidays: makePublicHolidayDefault(),
    holidayDates: {},
  };
}

function cleanText(value: unknown) {
  return String(value ?? "").trim();
}

function safeMinute(value: unknown, fallback: number) {
  const n = readTimeMinutes(value);

  if (n === null || !Number.isFinite(n)) return fallback;

  return Math.max(0, Math.min(1439, Math.round(n)));
}

function readTimeStringMinutes(value: string) {
  const clean = value.trim().toLowerCase();

  if (!clean) return null;

  if (/^\d+(\.\d+)?$/.test(clean)) {
    const n = Number(clean);
    return Number.isFinite(n) ? n : null;
  }

  const match = clean.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/);

  if (!match) return null;

  let hour = Number(match[1]);
  const minute = match[2] ? Number(match[2]) : 0;
  const suffix = match[3];

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (minute < 0 || minute > 59) return null;

  if (suffix === "am") {
    if (hour < 1 || hour > 12) return null;
    if (hour === 12) hour = 0;
  } else if (suffix === "pm") {
    if (hour < 1 || hour > 12) return null;
    if (hour !== 12) hour += 12;
  } else if (hour < 0 || hour > 23) {
    return null;
  }

  return hour * 60 + minute;
}

function readDateLikeMinutes(value: unknown) {
  if (value instanceof Date) {
    return value.getHours() * 60 + value.getMinutes();
  }

  if (
    value &&
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate?: unknown }).toDate === "function"
  ) {
    const date = (value as { toDate: () => Date }).toDate();
    return date.getHours() * 60 + date.getMinutes();
  }

  return null;
}

function readTimeMinutes(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") return readTimeStringMinutes(value);

  return readDateLikeMinutes(value);
}

function readFirstValue(
  source: Record<string, unknown>,
  keys: string[]
): unknown {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      return source[key];
    }
  }

  return undefined;
}

function isDateKey(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (!Number.isInteger(year) || year < 1900 || year > 3000) return false;
  if (!Number.isInteger(month) || month < 1 || month > 12) return false;
  if (!Number.isInteger(day) || day < 1 || day > 31) return false;

  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function sanitizeBusinessHoursDay(
  value: unknown,
  fallback: BusinessHoursDay
): BusinessHoursDay {
  if (!value || typeof value !== "object") return fallback;

  const raw = value as Partial<BusinessHoursDay>;

  const isOpen =
    typeof raw.isOpen === "boolean" ? raw.isOpen : fallback.isOpen;

  if (!isOpen) {
    return {
      isOpen: false,
      openMinutes: null,
      closeMinutes: null,
    };
  }

  const rawRecord = value as Record<string, unknown>;
  const openValue = readFirstValue(rawRecord, [
    "openMinutes",
    "openTime",
    "openingTime",
    "opensAt",
    "startTime",
  ]);
  const closeValue = readFirstValue(rawRecord, [
    "closeMinutes",
    "closeTime",
    "closingTime",
    "closesAt",
    "endTime",
  ]);

  return {
    isOpen: true,
    openMinutes: safeMinute(openValue, fallback.openMinutes ?? 480),
    closeMinutes: safeMinute(closeValue, fallback.closeMinutes ?? 1080),
  };
}

export function sanitizePublicHolidayHours(
  value: unknown
): PublicHolidayHours {
  const fallback = makePublicHolidayDefault();

  if (!value || typeof value !== "object") return fallback;

  const raw = value as Partial<PublicHolidayHours>;

  const mode = raw.mode === "custom" ? "custom" : "closed";

  return {
    mode,
    customDay: sanitizeBusinessHoursDay(raw.customDay, fallback.customDay),
  };
}

function sanitizeHolidayDates(value: unknown) {
  const out: Record<string, BusinessHoursDay> = {};
  const fallbackDay = makeDay(true);

  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return out;
  }

  const raw = value as Record<string, unknown>;

  for (const [dateKey, rawDay] of Object.entries(raw)) {
    if (!isDateKey(dateKey)) continue;
    if (!rawDay || typeof rawDay !== "object" || Array.isArray(rawDay)) {
      continue;
    }

    const day = sanitizeDateOverrideDay(rawDay);
    out[dateKey] = day || sanitizeBusinessHoursDay(rawDay, fallbackDay);
  }

  return out;
}

export function sanitizeBusinessHours(value: unknown): BusinessHours {
  const fallback = createDefaultBusinessHours();

  if (!value || typeof value !== "object") return fallback;

  const raw = value as Partial<BusinessHours>;
  const rawWeekly =
    raw.weekly && typeof raw.weekly === "object" ? raw.weekly : {};

  const weekly = {} as BusinessHours["weekly"];

  for (const key of DAY_KEYS) {
    const fallbackDay = fallback.weekly[key];

    const rawDay =
      (rawWeekly as Record<string, unknown>)[key] &&
      typeof (rawWeekly as Record<string, unknown>)[key] === "object"
        ? ((rawWeekly as Record<string, unknown>)[key] as Partial<BusinessHoursDay>)
        : {};

    weekly[key] = sanitizeBusinessHoursDay(rawDay, fallbackDay);
  }

  return {
    timezone: cleanText(raw.timezone) || "Africa/Nairobi",
    sameHoursEveryDay:
      typeof raw.sameHoursEveryDay === "boolean"
        ? raw.sameHoursEveryDay
        : true,
    is24Hours: !!raw.is24Hours,
    temporarilyClosed: !!raw.temporarilyClosed,
    weekly,
    publicHolidays: sanitizePublicHolidayHours(raw.publicHolidays),
    holidayDates: sanitizeHolidayDates(raw.holidayDates),
  };
}

export function formatTimeLabel(minutes: number | null | undefined) {
  if (typeof minutes !== "number" || !Number.isFinite(minutes)) return "";

  const safe = Math.max(0, Math.min(1439, Math.round(minutes)));
  const hour24 = Math.floor(safe / 60);
  const mins = safe % 60;

  const suffix = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;

  return `${hour12}:${String(mins).padStart(2, "0")} ${suffix}`;
}

function getZonedNowParts(date: Date, timezone: string) {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || "Africa/Nairobi",
      weekday: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });

    const parts = formatter.formatToParts(date);

    const weekday = parts.find((p) => p.type === "weekday")?.value || "";
    const year = Number(parts.find((p) => p.type === "year")?.value || 0);
    const month = Number(parts.find((p) => p.type === "month")?.value || 0);
    const day = Number(parts.find((p) => p.type === "day")?.value || 0);
    const hour = Number(parts.find((p) => p.type === "hour")?.value || 0);
    const minute = Number(parts.find((p) => p.type === "minute")?.value || 0);

    return {
      dayIndex: SHORT_WEEKDAY_TO_INDEX[weekday] ?? date.getDay(),
      minutes: hour * 60 + minute,
      year,
      month,
      day,
      dateKey: `${year}-${String(month).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`,
    };
  } catch {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return {
      dayIndex: date.getDay(),
      minutes: date.getHours() * 60 + date.getMinutes(),
      year,
      month,
      day,
      dateKey: `${year}-${String(month).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`,
    };
  }
}

/**
 * Version 1:
 * Detects Kenya fixed-date public holidays.
 *
 * Later, add an admin-managed holiday calendar for movable/gazetted holidays
 * like Eid, Good Friday, Easter Monday, and special government-declared dates.
 */
function isKenyaFixedPublicHoliday(month: number, day: number) {
  const key = `${String(month).padStart(2, "0")}-${String(day).padStart(
    2,
    "0"
  )}`;

  return [
    "01-01", // New Year's Day
    "05-01", // Labour Day
    "06-01", // Madaraka Day
    "10-10", // Mazingira Day
    "10-20", // Mashujaa Day
    "12-12", // Jamhuri Day
    "12-25", // Christmas Day
    "12-26", // Boxing Day
  ].includes(key);
}

function isConfiguredPublicHoliday(source: any, dateKey: string) {
  const raw =
    source?.publicHolidayDates ||
    source?.holidayDates ||
    source?.publicHolidayCalendar?.dates;

  if (Array.isArray(raw)) {
    return raw.map(String).includes(dateKey);
  }

  if (raw && typeof raw === "object") {
    return !!(raw as Record<string, unknown>)[dateKey];
  }

  return false;
}

function isPublicHolidayToday(args: {
  source: any;
  rawHours: any;
  month: number;
  day: number;
  dateKey: string;
}) {
  return (
    isConfiguredPublicHoliday(args.source, args.dateKey) ||
    isConfiguredPublicHoliday(args.rawHours, args.dateKey) ||
    isKenyaFixedPublicHoliday(args.month, args.day)
  );
}

function sanitizeDateOverrideDay(value: unknown) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const raw = value as Record<string, unknown>;

  if (raw.mode === "closed" || raw.closed === true || raw.isClosed === true) {
    return sanitizeBusinessHoursDay({ isOpen: false }, makeDay(true));
  }

  if (
    raw.mode === "custom" &&
    raw.customDay &&
    typeof raw.customDay === "object" &&
    !Array.isArray(raw.customDay)
  ) {
    return sanitizeBusinessHoursDay(raw.customDay, makeDay(true));
  }

  return sanitizeBusinessHoursDay(raw, makeDay(true));
}

function readDateOverrideDay(value: unknown, dateKey: string) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  const raw = value as Record<string, unknown>;
  const rawDay = raw[dateKey];

  return sanitizeDateOverrideDay(rawDay);
}

function getHolidayDateOverride(args: {
  hours: BusinessHours;
  rawHours: any;
  source: any;
  dateKey: string;
}) {
  const direct = args.hours.holidayDates[args.dateKey];
  if (direct) return direct;

  return (
    readDateOverrideDay(args.rawHours?.specialHours, args.dateKey) ||
    readDateOverrideDay(args.rawHours?.holidayDates, args.dateKey) ||
    readDateOverrideDay(args.rawHours?.holidays, args.dateKey) ||
    readDateOverrideDay(args.source?.specialHours, args.dateKey) ||
    readDateOverrideDay(args.source?.holidayDates, args.dateKey) ||
    readDateOverrideDay(args.source?.holidays, args.dateKey) ||
    null
  );
}

function getDayByIndex(hours: BusinessHours, dayIndex: number) {
  const key = DAY_KEYS[((dayIndex % 7) + 7) % 7];
  return hours.weekly[key];
}

function isOpenFromPreviousOvernight(
  previousDay: BusinessHoursDay,
  currentMinutes: number
) {
  if (!previousDay.isOpen) return null;
  if (
    typeof previousDay.openMinutes !== "number" ||
    typeof previousDay.closeMinutes !== "number"
  ) {
    return null;
  }

  const open = previousDay.openMinutes;
  const close = previousDay.closeMinutes;

  if (open > close && currentMinutes < close) {
    return {
      closeMinutes: close,
    };
  }

  return null;
}

function isOpenFromToday(today: BusinessHoursDay, currentMinutes: number) {
  if (!today.isOpen) return null;
  if (
    typeof today.openMinutes !== "number" ||
    typeof today.closeMinutes !== "number"
  ) {
    return null;
  }

  const open = today.openMinutes;
  const close = today.closeMinutes;

  if (open === close) {
    return {
      closeMinutes: null,
      is24Hours: true,
    };
  }

  if (open < close && currentMinutes >= open && currentMinutes < close) {
    return {
      closeMinutes: close,
      is24Hours: false,
    };
  }

  if (open > close && currentMinutes >= open) {
    return {
      closeMinutes: close,
      is24Hours: false,
    };
  }

  return null;
}

function relativeDayLabel(currentDayIndex: number, offset: number) {
  if (offset === 0) return "today";
  if (offset === 1) return "tomorrow";

  const nextIndex = (currentDayIndex + offset) % 7;
  return BUSINESS_HOURS_DAYS[nextIndex].label;
}

function findNextOpening(
  hours: BusinessHours,
  currentDayIndex: number,
  currentMinutes: number
) {
  for (let offset = 0; offset <= 7; offset++) {
    const dayIndex = (currentDayIndex + offset) % 7;
    const day = getDayByIndex(hours, dayIndex);

    if (!day.isOpen) continue;
    if (
      typeof day.openMinutes !== "number" ||
      typeof day.closeMinutes !== "number"
    ) {
      continue;
    }

    const open = day.openMinutes;

    if (offset === 0) {
      if (currentMinutes < open) {
        return {
          offset,
          dayIndex,
          openMinutes: open,
        };
      }

      continue;
    }

    return {
      offset,
      dayIndex,
      openMinutes: open,
    };
  }

  return null;
}

function getPublicHolidayStatus(
  publicHolidays: PublicHolidayHours,
  currentMinutes: number
): BusinessOpenStatus {
  if (publicHolidays.mode === "closed") {
    return {
      isOpen: false,
      tone: "closed",
      label: "Closed today",
      detail: "Public holiday",
      fullText: "Closed today · Public holiday",
    };
  }

  const day = publicHolidays.customDay;

  if (!day.isOpen) {
    return {
      isOpen: false,
      tone: "closed",
      label: "Closed today",
      detail: "Public holiday",
      fullText: "Closed today · Public holiday",
    };
  }

  if (
    typeof day.openMinutes !== "number" ||
    typeof day.closeMinutes !== "number"
  ) {
    return {
      isOpen: false,
      tone: "unknown",
      label: "Holiday hours not set",
      detail: "",
      fullText: "Holiday hours not set",
    };
  }

  const open = day.openMinutes;
  const close = day.closeMinutes;

  if (open === close) {
    return {
      isOpen: true,
      tone: "open",
      label: "Open 24 hours",
      detail: "Public holiday",
      fullText: "Open 24 hours · Public holiday",
    };
  }

  if (open < close) {
    if (currentMinutes >= open && currentMinutes < close) {
      const detail = `Holiday hours · Closes ${formatTimeLabel(close)}`;

      return {
        isOpen: true,
        tone: "open",
        label: "Open now",
        detail,
        fullText: `Open now · ${detail}`,
      };
    }

    if (currentMinutes < open) {
      const detail = `Holiday hours · Opens today ${formatTimeLabel(open)}`;

      return {
        isOpen: false,
        tone: "closed",
        label: "Closed now",
        detail,
        fullText: `Closed now · ${detail}`,
      };
    }

    return {
      isOpen: false,
      tone: "closed",
      label: "Closed now",
      detail: "Holiday hours ended",
      fullText: "Closed now · Holiday hours ended",
    };
  }

  // Overnight holiday hours, e.g. 8:00 PM - 2:00 AM.
  if (open > close) {
    if (currentMinutes >= open || currentMinutes < close) {
      const detail = `Holiday hours · Closes ${formatTimeLabel(close)}`;

      return {
        isOpen: true,
        tone: "open",
        label: "Open now",
        detail,
        fullText: `Open now · ${detail}`,
      };
    }

    const detail = `Holiday hours · Opens today ${formatTimeLabel(open)}`;

    return {
      isOpen: false,
      tone: "closed",
      label: "Closed now",
      detail,
      fullText: `Closed now · ${detail}`,
    };
  }

  return {
    isOpen: false,
    tone: "closed",
    label: "Closed now",
    detail: "Public holiday",
    fullText: "Closed now · Public holiday",
  };
}

export function getBusinessOpenStatus(
  businessOrHours: any,
  now = new Date()
): BusinessOpenStatus {
  const rawHours =
    businessOrHours?.businessHours &&
    typeof businessOrHours.businessHours === "object"
      ? businessOrHours.businessHours
      : businessOrHours?.weekly
      ? businessOrHours
      : null;

  if (!rawHours) {
    return {
      isOpen: false,
      tone: "unknown",
      label: "Hours not set",
      detail: "",
      fullText: "Hours not set",
    };
  }

  const hours = sanitizeBusinessHours(rawHours);

  if (hours.temporarilyClosed) {
    return {
      isOpen: false,
      tone: "closed",
      label: "Temporarily closed",
      detail: "",
      fullText: "Temporarily closed",
    };
  }

  const { dayIndex, minutes, month, day, dateKey } = getZonedNowParts(
    now,
    hours.timezone
  );

  const isPublicHoliday = isPublicHolidayToday({
    source: businessOrHours,
    rawHours,
    month,
    day,
    dateKey,
  });

  const holidayDateOverride = getHolidayDateOverride({
    hours,
    rawHours,
    source: businessOrHours,
    dateKey,
  });

  if (holidayDateOverride) {
    return getPublicHolidayStatus(
      {
        mode: "custom",
        customDay: holidayDateOverride,
      },
      minutes
    );
  }

  if (isPublicHoliday) {
    return getPublicHolidayStatus(hours.publicHolidays, minutes);
  }

  if (hours.is24Hours) {
    return {
      isOpen: true,
      tone: "open",
      label: "Open 24 hours",
      detail: "",
      fullText: "Open 24 hours",
    };
  }

  const today = getDayByIndex(hours, dayIndex);
  const previousDay = getDayByIndex(hours, dayIndex - 1);

  const previousOvernight = isOpenFromPreviousOvernight(previousDay, minutes);
  if (previousOvernight) {
    const detail = `Closes ${formatTimeLabel(previousOvernight.closeMinutes)}`;

    return {
      isOpen: true,
      tone: "open",
      label: "Open now",
      detail,
      fullText: `Open now · ${detail}`,
    };
  }

  const todayOpen = isOpenFromToday(today, minutes);
  if (todayOpen) {
    if (todayOpen.is24Hours) {
      return {
        isOpen: true,
        tone: "open",
        label: "Open 24 hours",
        detail: "",
        fullText: "Open 24 hours",
      };
    }

    const detail = `Closes ${formatTimeLabel(todayOpen.closeMinutes)}`;

    return {
      isOpen: true,
      tone: "open",
      label: "Open now",
      detail,
      fullText: `Open now · ${detail}`,
    };
  }

  const nextOpening = findNextOpening(hours, dayIndex, minutes);

  if (!nextOpening) {
    return {
      isOpen: false,
      tone: "closed",
      label: "Closed now",
      detail: "No opening hours available",
      fullText: "Closed now",
    };
  }

  const dayLabel = relativeDayLabel(dayIndex, nextOpening.offset);
  const detail = `Opens ${dayLabel} ${formatTimeLabel(nextOpening.openMinutes)}`;

  return {
    isOpen: false,
    tone: "closed",
    label: "Closed now",
    detail,
    fullText: `Closed now · ${detail}`,
  };
}
